import { Button, Popover, Stack, Text } from '@mantine/core';
import { useHeartRhythmOverview } from 'hooks/api/useHeartRhythmData';
import { HeartRhythmMeasurementDiagnosis } from 'models/heartRhythm/measurementDiagnosis';
import { useTranslation } from 'react-i18next';
import { roundToTwoDecimals } from 'utils/helpers';

export const MoreMetricsPopOver = () => {
  const { t } = useTranslation();
  const { data: measurements } = useHeartRhythmOverview();

  const getAverage = (values?: number[]) => {
    if (!values) return 0;
    const sum: number = values ? values.reduce((accumulator, currentValue) => accumulator + currentValue, 0) : 0;

    return sum / values.length || 0;
  };

  const aggregatedHeartrates = measurements
    ?.map((measurement) => measurement.data?.heartrate)
    .filter((num): num is number => num !== undefined && num !== -1);

  const aggregatedHeartratesAverage = getAverage(aggregatedHeartrates);

  const afMeasurement =
    measurements?.filter(
      (measurement) => measurement.data?.diagnosis?.label[0] === HeartRhythmMeasurementDiagnosis.atrial_fibrillation,
    ) || [];

  const aggregatedAfMeasurement = afMeasurement
    ?.map((measurement) => measurement.data?.heartrate)
    .filter((num): num is number => num !== undefined && num !== -1);

  const afMeasurementAverage: number = getAverage(aggregatedAfMeasurement);

  const sinusMeasurement =
    measurements?.filter(
      (measurement) => measurement.data?.diagnosis?.label[0] === HeartRhythmMeasurementDiagnosis.sinus,
    ) || [];

  const aggregatedSinusMeasurement = sinusMeasurement
    ?.map((measurement) => measurement.data?.heartrate)
    .filter((num): num is number => num !== undefined && num !== -1);

  const sinusMeasurementAverage: number = getAverage(aggregatedSinusMeasurement);

  return (
    <Popover position={'bottom'} shadow={'md'}>
      <Popover.Target>
        <Button variant={'subtle'}>{t('patients_more_metrics')}</Button>
      </Popover.Target>
      <Popover.Dropdown>
        <Stack>
          <Text size={'sm'} fw={'bold'}>
            {`${t('patients_highest_heart_rate')}: `}
            {aggregatedHeartrates?.length ? Math.max(...aggregatedHeartrates) : 0} {t('bpm_label')}
          </Text>
          <Text size={'sm'} fw={'bold'}>
            {`${t('patients_lowest_heart_rate')}: `}
            {aggregatedHeartrates?.length ? Math.min(...aggregatedHeartrates) : 0} {t('bpm_label')}
          </Text>
          <Text size={'sm'} fw={'bold'}>
            {`${t('patients_average_heart_rate')}:`} {roundToTwoDecimals(aggregatedHeartratesAverage) || 0}{' '}
            {t('bpm_label')}
          </Text>
          <Text size={'sm'} fw={'bold'}>
            {`${t('patients_average_heart_rate_sinus')}: `}
            {roundToTwoDecimals(sinusMeasurementAverage) || 0} {t('bpm_label')}
          </Text>
          <Text size={'sm'} fw={'bold'}>
            {`${t('patients_average_heart_rate_af')}: `}
            {roundToTwoDecimals(Math.round(afMeasurementAverage || 0))} {t('bpm_label')}
          </Text>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
};
